import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { SelectInput, TextareaInput, TextInput } from "@/components/ui/Field";
import {
  GdRichTextarea,
  type GdRichTextInsertHandle,
} from "@/components/ui/GdRichTextarea";
import {
  HtmlMailBodyEditor,
  type HtmlMailBodyInsertHandle,
} from "@/components/ui/HtmlMailBodyEditor";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import { useCurrentUser, userIsBuero } from "@/lib/auth";
import { DOKUMENT_TYP_LABELS, type DokumentTyp } from "@/lib/dokumente";
import {
  DEFAULT_ANGEBOT_NACHFASS_BODY,
  DEFAULT_ANGEBOT_NACHFASS_SUBJECT,
  DEFAULT_GD_EMAIL_BODY,
  DEFAULT_GD_EMAIL_SUBJECT,
  deleteGdEmailVorlage,
  listGdEmailVorlagen,
  saveGdEmailVorlage,
} from "@/lib/gdEmailVorlagen";
import {
  fetchGdPlaceholderCatalog,
  type GdPlaceholderCategory,
} from "@/lib/gdPlaceholders";
import {
  deleteGdStandardtext,
  listGdStandardtexte,
  saveGdStandardtext,
} from "@/lib/gdStandardtexte";
import { listMahnstufen, updateMahnstufe, type Mahnstufe } from "@/lib/mahnwesen";
import { toast } from "@/lib/toast";
import { HourlyRatesSection } from "@/pages/admin/einstellungen/sections/hero/RateMappingSections";
import { GdPlaceholderPicker } from "@/pages/dokumente/GdPlaceholderPicker";
import "@/pages/dokumente/dokumente.css";

const DOKUMENT_TYPEN = (Object.keys(DOKUMENT_TYP_LABELS) as DokumentTyp[]).filter(
  (typ) => typ !== "stueckliste" && typ !== "stundennachweis",
);

const MAHNSTUFE_PREFIX = "mahnstufe:";

function mahnstufeOptionValue(id: number): string {
  return `${MAHNSTUFE_PREFIX}${id}`;
}

function parseMahnstufeId(value: string): number | null {
  if (!value.startsWith(MAHNSTUFE_PREFIX)) return null;
  const id = Number(value.slice(MAHNSTUFE_PREFIX.length));
  return Number.isFinite(id) ? id : null;
}

type InsertTarget =
  | "einleitung"
  | "schluss"
  | "mail-subject"
  | "mail-body"
  | "nachfass-subject"
  | "nachfass-body";

export function DokumenteEinstellungenPage() {
  const { user } = useCurrentUser();
  const isBuero = userIsBuero(user);
  const [dokumentTyp, setDokumentTyp] = useState<string>("angebot");
  const [mahnstufen, setMahnstufen] = useState<Mahnstufe[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [einleitung, setEinleitung] = useState("");
  const [schlusstext, setSchlusstext] = useState("");
  const [hasEinleitung, setHasEinleitung] = useState(false);
  const [hasSchluss, setHasSchluss] = useState(false);
  const [savingStandard, setSavingStandard] = useState<"einleitung" | "schlusstext" | null>(null);

  const [mailSubject, setMailSubject] = useState("");
  const [mailBody, setMailBody] = useState("");
  const [hasMailVorlage, setHasMailVorlage] = useState(false);
  const [savingMail, setSavingMail] = useState(false);

  const [nachfassSubject, setNachfassSubject] = useState("");
  const [nachfassBody, setNachfassBody] = useState("");
  const [hasNachfassVorlage, setHasNachfassVorlage] = useState(false);
  const [savingNachfass, setSavingNachfass] = useState(false);

  const [categories, setCategories] = useState<GdPlaceholderCategory[]>([]);
  const [activeCategory, setActiveCategory] = useState("");
  const [insertTarget, setInsertTarget] = useState<InsertTarget>("mail-body");

  const einleitungRef = useRef<GdRichTextInsertHandle>(null);
  const schlussRef = useRef<GdRichTextInsertHandle>(null);
  const mailBodyRef = useRef<HtmlMailBodyInsertHandle>(null);
  const nachfassBodyRef = useRef<HtmlMailBodyInsertHandle>(null);
  const mailSubjectId = "gd-einstellungen-mail-subject";
  const mailBodyId = "gd-einstellungen-mail-body";
  const nachfassSubjectId = "gd-einstellungen-nachfass-subject";

  const selectedMahnstufeId = parseMahnstufeId(dokumentTyp);
  const isMahnstufe = selectedMahnstufeId != null;
  const selectedMahnstufe = useMemo(
    () => (selectedMahnstufeId == null ? null : mahnstufen.find((stufe) => stufe.id === selectedMahnstufeId) ?? null),
    [mahnstufen, selectedMahnstufeId],
  );
  const typLabel = selectedMahnstufe?.bezeichnung || DOKUMENT_TYP_LABELS[dokumentTyp as DokumentTyp] || "Dokument";

  useEffect(() => {
    let cancelled = false;
    void listMahnstufen()
      .then((stufen) => {
        if (!cancelled) setMahnstufen(stufen);
      })
      .catch(() => {
        if (!cancelled) setMahnstufen([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    void fetchGdPlaceholderCatalog().then((cats) => {
      if (cancelled) return;
      setCategories(cats);
      setActiveCategory((prev) => prev || cats[0]?.key || "");
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isMahnstufe) return;
    setActiveCategory((prev) => (categories.some((c) => c.key === "mahnung") ? "mahnung" : prev));
    setInsertTarget("einleitung");
  }, [isMahnstufe, categories]);

  useEffect(() => {
    if (isMahnstufe && !selectedMahnstufe) {
      setLoading(true);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    if (selectedMahnstufe) {
      setEinleitung(selectedMahnstufe.einleitung || "");
      setSchlusstext(selectedMahnstufe.schlusstext || "");
      setHasEinleitung(Boolean(selectedMahnstufe.einleitung?.trim()));
      setHasSchluss(Boolean(selectedMahnstufe.schlusstext?.trim()));
      setMailSubject(selectedMahnstufe.email_betreff || "");
      setMailBody(selectedMahnstufe.email_text || "");
      setHasMailVorlage(
        Boolean(selectedMahnstufe.email_betreff?.trim() || selectedMahnstufe.email_text?.trim()),
      );
      setLoading(false);
      return;
    }

    void Promise.all([
      listGdStandardtexte(dokumentTyp as DokumentTyp),
      listGdEmailVorlagen(dokumentTyp as DokumentTyp),
      dokumentTyp === "angebot" ? listGdEmailVorlagen("angebot_nachfass") : Promise.resolve([]),
    ])
      .then(([standards, mails, nachfassMails]) => {
        if (cancelled) return;
        const ein = standards.find((r) => r.bereich === "einleitung");
        const schluss = standards.find((r) => r.bereich === "schlusstext");
        setEinleitung(ein?.inhalt || "");
        setSchlusstext(schluss?.inhalt || "");
        setHasEinleitung(!!ein);
        setHasSchluss(!!schluss);

        const mail = mails[0];
        if (mail) {
          setMailSubject(mail.subject || "");
          setMailBody(mail.body_html || "");
          setHasMailVorlage(true);
        } else {
          setMailSubject(DEFAULT_GD_EMAIL_SUBJECT);
          setMailBody(DEFAULT_GD_EMAIL_BODY);
          setHasMailVorlage(false);
        }

        const nachfass = nachfassMails[0];
        if (nachfass) {
          setNachfassSubject(nachfass.subject || "");
          setNachfassBody(nachfass.body_html || "");
          setHasNachfassVorlage(true);
        } else {
          setNachfassSubject(DEFAULT_ANGEBOT_NACHFASS_SUBJECT);
          setNachfassBody(DEFAULT_ANGEBOT_NACHFASS_BODY);
          setHasNachfassVorlage(false);
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : "Einstellungen konnten nicht geladen werden.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dokumentTyp, isMahnstufe, selectedMahnstufe]);

  function insertIntoSubject(
    token: string,
    inputId: string,
    value: string,
    setValue: (next: string) => void,
  ) {
    const input = document.getElementById(inputId) as HTMLInputElement | null;
    if (!input) {
      setValue(`${value}${token}`);
      return;
    }
    const start = input.selectionStart ?? value.length;
    const end = input.selectionEnd ?? start;
    const next = `${value.slice(0, start)}${token}${value.slice(end)}`;
    setValue(next);
    requestAnimationFrame(() => {
      input.focus();
      const pos = start + token.length;
      input.setSelectionRange(pos, pos);
    });
  }

  function insertToken(token: string) {
    if (insertTarget === "einleitung") {
      einleitungRef.current?.insertText(token);
      return;
    }
    if (insertTarget === "schluss") {
      schlussRef.current?.insertText(token);
      return;
    }
    if (insertTarget === "mail-body") {
      if (isMahnstufe) {
        insertIntoSubject(token, mailBodyId, mailBody, setMailBody);
        return;
      }
      mailBodyRef.current?.insertText(token);
      return;
    }
    if (insertTarget === "nachfass-body") {
      nachfassBodyRef.current?.insertText(token);
      return;
    }
    if (insertTarget === "nachfass-subject") {
      insertIntoSubject(token, nachfassSubjectId, nachfassSubject, setNachfassSubject);
      return;
    }
    insertIntoSubject(token, mailSubjectId, mailSubject, setMailSubject);
  }

  async function persistMahnstufe(patch: Partial<Mahnstufe>): Promise<Mahnstufe | null> {
    if (!selectedMahnstufe) return null;
    const updated = await updateMahnstufe(selectedMahnstufe.id, {
      ...selectedMahnstufe,
      einleitung,
      schlusstext,
      email_betreff: mailSubject,
      email_text: mailBody,
      ...patch,
    });
    setMahnstufen((prev) => prev.map((stufe) => (stufe.id === updated.id ? updated : stufe)));
    return updated;
  }

  async function saveStandard(bereich: "einleitung" | "schlusstext") {
    setSavingStandard(bereich);
    try {
      const inhalt = bereich === "einleitung" ? einleitung : schlusstext;
      if (isMahnstufe) {
        await persistMahnstufe({
          einleitung: bereich === "einleitung" ? inhalt : einleitung,
          schlusstext: bereich === "schlusstext" ? inhalt : schlusstext,
        });
      } else {
        await saveGdStandardtext(dokumentTyp as DokumentTyp, bereich, inhalt);
      }
      if (bereich === "einleitung") setHasEinleitung(true);
      else setHasSchluss(true);
      toast.success(
        bereich === "einleitung"
          ? "Standard-Einleitungstext gespeichert."
          : "Standard-Schlusstext gespeichert.",
      );
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSavingStandard(null);
    }
  }

  async function clearStandard(bereich: "einleitung" | "schlusstext") {
    if (!window.confirm("Standardtext für diesen Dokumenttyp löschen?")) return;
    try {
      if (isMahnstufe) {
        await persistMahnstufe({ [bereich]: "" });
      } else {
        await deleteGdStandardtext(dokumentTyp as DokumentTyp, bereich);
      }
      if (bereich === "einleitung") {
        setEinleitung("");
        setHasEinleitung(false);
      } else {
        setSchlusstext("");
        setHasSchluss(false);
      }
      toast.success("Standardtext gelöscht.");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Löschen fehlgeschlagen.");
    }
  }

  async function saveMail() {
    setSavingMail(true);
    try {
      if (isMahnstufe) {
        await persistMahnstufe({
          email_betreff: mailSubject,
          email_text: mailBody,
        });
      } else {
        const bodyHtml = mailBodyRef.current?.getHtml() ?? mailBody;
        setMailBody(bodyHtml);
        await saveGdEmailVorlage(dokumentTyp as DokumentTyp, {
          subject: mailSubject,
          body_html: bodyHtml,
        });
      }
      setHasMailVorlage(true);
      toast.success("E-Mail-Vorlage gespeichert.");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSavingMail(false);
    }
  }

  async function clearMail() {
    if (!window.confirm("E-Mail-Vorlage für diesen Dokumenttyp löschen? Es gilt wieder der System-Standard.")) {
      return;
    }
    try {
      if (isMahnstufe) {
        await persistMahnstufe({ email_betreff: "", email_text: "" });
        setMailSubject("");
        setMailBody("");
      } else {
        if (hasMailVorlage) await deleteGdEmailVorlage(dokumentTyp as DokumentTyp);
        setMailSubject(DEFAULT_GD_EMAIL_SUBJECT);
        setMailBody(DEFAULT_GD_EMAIL_BODY);
      }
      setHasMailVorlage(false);
      toast.success("E-Mail-Vorlage zurückgesetzt.");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Löschen fehlgeschlagen.");
    }
  }

  async function saveNachfass() {
    setSavingNachfass(true);
    try {
      const bodyHtml = nachfassBodyRef.current?.getHtml() ?? nachfassBody;
      setNachfassBody(bodyHtml);
      await saveGdEmailVorlage("angebot_nachfass", {
        subject: nachfassSubject,
        body_html: bodyHtml,
      });
      setHasNachfassVorlage(true);
      toast.success("Nachfass-E-Mail-Vorlage gespeichert.");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSavingNachfass(false);
    }
  }

  async function clearNachfass() {
    if (
      !window.confirm(
        "Nachfass-E-Mail-Vorlage löschen? Es gilt wieder der System-Standard für Erinnerungen.",
      )
    ) {
      return;
    }
    try {
      if (hasNachfassVorlage) await deleteGdEmailVorlage("angebot_nachfass");
      setNachfassSubject(DEFAULT_ANGEBOT_NACHFASS_SUBJECT);
      setNachfassBody(DEFAULT_ANGEBOT_NACHFASS_BODY);
      setHasNachfassVorlage(false);
      toast.success("Nachfass-E-Mail-Vorlage zurückgesetzt.");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Löschen fehlgeschlagen.");
    }
  }

  return (
    <AppShell
      title="Dokumente · Einstellungen"
      actions={
        isBuero ? undefined : (
          <Link
            to="/dokumente"
            className="inline-flex items-center rounded-lg border border-[var(--eh-color-border)] px-3 py-2 text-sm font-medium text-label hover:bg-[var(--eh-color-surface-muted)]"
          >
            ← Dokumente
          </Link>
        )
      }
    >
      <div className="space-y-4 md:space-y-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-label">Einstellungen</h2>
            <p className="text-sm text-label-secondary">
              Monteur-Stundensätze, Standardtexte, E-Mail-Vorlagen und Layout für Geschäftsdokumente.
            </p>
          </div>
          <SelectInput
            label="Dokumenttyp"
            value={dokumentTyp}
            onChange={(e) => setDokumentTyp(e.target.value)}
            className="min-w-[220px]"
          >
            <optgroup label="Geschäftsdokumente">
              {DOKUMENT_TYPEN.map((typ) => (
                <option key={typ} value={typ}>
                  {DOKUMENT_TYP_LABELS[typ]}
                </option>
              ))}
            </optgroup>
            {mahnstufen.length > 0 ? (
              <optgroup label="Mahnwesen">
                {mahnstufen.map((stufe) => (
                  <option key={stufe.id} value={mahnstufeOptionValue(stufe.id)}>
                    {stufe.bezeichnung}
                    {stufe.aktiv ? "" : " (inaktiv)"}
                  </option>
                ))}
              </optgroup>
            ) : null}
          </SelectInput>
        </div>

        {error ? <Alert variant="error" title="Fehler">{error}</Alert> : null}

        <HourlyRatesSection />

        <Link
          to="/dokumente/layout"
          className="group flex items-center justify-between gap-3 rounded-ios border border-separator bg-card px-5 py-4 shadow-ios transition hover:-translate-y-0.5 hover:border-accent hover:shadow-ios-md"
        >
          <div>
            <h3 className="text-base font-semibold text-label">Layout-Einstellungen</h3>
            <p className="text-sm text-label-secondary">
              Briefpapier, Seitenränder, Schrift und Nummernkreise.
            </p>
          </div>
          <span aria-hidden className="text-lg text-label-tertiary transition group-hover:translate-x-0.5 group-hover:text-accent">
            ›
          </span>
        </Link>

        {loading ? (
          <div className="flex justify-center py-10">
            <Spinner />
          </div>
        ) : (
          <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_20rem]">
            <div className="space-y-4">
              <Card
                title={`Einleitungs- und Schlusstexte (${typLabel})`}
                description={
                  isMahnstufe ? (
                    <>
                      Vorlage beim Erstellen einer Mahnung dieser Stufe. Weitere Texte (Anrede, Haupttext)
                      liegen unter{" "}
                      <Link to="/dokumente/mahnwesen" className="text-accent hover:underline">
                        Mahnwesen → Einstellungen
                      </Link>
                      .
                    </>
                  ) : (
                    "Werden beim Anlegen und Umwandeln neuer Dokumente dieses Typs vorausgefüllt – auch wenn Positionen aus einem Quelldokument kommen."
                  )
                }
              >
                <div className="space-y-5">
                  <div className="space-y-2" onFocusCapture={() => setInsertTarget("einleitung")}>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <h4 className="text-sm font-semibold text-label">Einleitungstext</h4>
                      <div className="flex flex-wrap gap-2">
                        {hasEinleitung ? (
                          <Button type="button" size="sm" variant="ghost" onClick={() => void clearStandard("einleitung")}>
                            Löschen
                          </Button>
                        ) : null}
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => void saveStandard("einleitung")}
                          loading={savingStandard === "einleitung"}
                        >
                          Speichern
                        </Button>
                      </div>
                    </div>
                    <GdRichTextarea
                      ref={einleitungRef}
                      value={einleitung}
                      onChange={(e) => setEinleitung(e.target.value)}
                      minRows={5}
                      placeholder="Standard-Einleitung mit Platzhaltern …"
                    />
                  </div>

                  <div className="space-y-2" onFocusCapture={() => setInsertTarget("schluss")}>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <h4 className="text-sm font-semibold text-label">Schlusstext</h4>
                      <div className="flex flex-wrap gap-2">
                        {hasSchluss ? (
                          <Button type="button" size="sm" variant="ghost" onClick={() => void clearStandard("schlusstext")}>
                            Löschen
                          </Button>
                        ) : null}
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => void saveStandard("schlusstext")}
                          loading={savingStandard === "schlusstext"}
                        >
                          Speichern
                        </Button>
                      </div>
                    </div>
                    <GdRichTextarea
                      ref={schlussRef}
                      value={schlusstext}
                      onChange={(e) => setSchlusstext(e.target.value)}
                      minRows={5}
                      placeholder="Standard-Schlusstext mit Platzhaltern …"
                    />
                  </div>
                </div>
              </Card>

              <Card
                title={`E-Mail-Vorlage (${typLabel})`}
                description={
                  isMahnstufe
                    ? "Vorlage beim Versand dieser Mahnstufe. Platzhalter wie {{Mahnung.gesamtforderung}} werden beim Erstellen ersetzt."
                    : "Vorlage beim Versenden eines Dokuments. Die Firmensignatur platzieren Sie mit dem Platzhalter {{Signatur}}."
                }
                actions={
                  <div className="flex flex-wrap gap-2">
                    {hasMailVorlage ? (
                      <Button type="button" size="sm" variant="ghost" onClick={() => void clearMail()}>
                        Zurücksetzen
                      </Button>
                    ) : null}
                    <Button type="button" size="sm" onClick={() => void saveMail()} loading={savingMail}>
                      Speichern
                    </Button>
                  </div>
                }
              >
                <div className="space-y-3">
                  {!hasMailVorlage ? (
                    <p className="text-xs text-label-secondary">
                      Noch keine eigene Vorlage gespeichert – angezeigt ist der vorgeschlagene Standard.
                    </p>
                  ) : null}
                  <div onFocusCapture={() => setInsertTarget("mail-subject")}>
                    <TextInput
                      id={mailSubjectId}
                      label="Betreff"
                      value={mailSubject}
                      onChange={(e) => setMailSubject(e.target.value)}
                      placeholder="{{Dokument.typ}} {{Dokument.nummer}}"
                    />
                  </div>
                  <div className="space-y-1" onFocusCapture={() => setInsertTarget("mail-body")}>
                    <span className="text-sm text-label-secondary">Nachricht</span>
                    {isMahnstufe ? (
                      <TextareaInput
                        id={mailBodyId}
                        value={mailBody}
                        onChange={(e) => setMailBody(e.target.value)}
                        rows={8}
                        placeholder="Mailtext mit Platzhaltern …"
                      />
                    ) : (
                      <HtmlMailBodyEditor
                        ref={mailBodyRef}
                        value={mailBody}
                        onChange={setMailBody}
                        minRows={8}
                        placeholder="Mailtext mit Platzhaltern …"
                      />
                    )}
                  </div>
                  <p className="text-xs text-label-tertiary">
                    Platzhalter werden beim Öffnen des Versanddialogs mit den Dokumentdaten gefüllt.
                    Signatur unter Admin → Einstellungen → Kommunikation bearbeiten und mit{" "}
                    <code className="text-[0.7rem]">{"{{Signatur}}"}</code> in der Vorlage platzieren.
                  </p>
                </div>
              </Card>

              {dokumentTyp === "angebot" && !isMahnstufe ? (
                <Card
                  title="E-Mail-Vorlage Nachfass (Angebot)"
                  description="Wird beim erneuten Versand aus der Übersicht „Angebote > 2 Wochen versendet“ verwendet."
                  actions={
                    <div className="flex flex-wrap gap-2">
                      {hasNachfassVorlage ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => void clearNachfass()}
                        >
                          Zurücksetzen
                        </Button>
                      ) : null}
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => void saveNachfass()}
                        loading={savingNachfass}
                      >
                        Speichern
                      </Button>
                    </div>
                  }
                >
                  <div className="space-y-3">
                    {!hasNachfassVorlage ? (
                      <p className="text-xs text-label-secondary">
                        Noch keine eigene Nachfass-Vorlage gespeichert – angezeigt ist der vorgeschlagene
                        Standard.
                      </p>
                    ) : null}
                    <div onFocusCapture={() => setInsertTarget("nachfass-subject")}>
                      <TextInput
                        id={nachfassSubjectId}
                        label="Betreff"
                        value={nachfassSubject}
                        onChange={(e) => setNachfassSubject(e.target.value)}
                        placeholder="Erinnerung: {{Dokument.nummer}}"
                      />
                    </div>
                    <div className="space-y-1" onFocusCapture={() => setInsertTarget("nachfass-body")}>
                      <span className="text-sm text-label-secondary">Nachricht</span>
                      <HtmlMailBodyEditor
                        ref={nachfassBodyRef}
                        value={nachfassBody}
                        onChange={setNachfassBody}
                        minRows={8}
                        placeholder="Nachfass-Mailtext mit Platzhaltern …"
                      />
                    </div>
                  </div>
                </Card>
              ) : null}
            </div>

            <Card
              title="Platzhalter"
              description={
                insertTarget === "einleitung"
                  ? "Einfügen in Einleitungstext"
                  : insertTarget === "schluss"
                    ? "Einfügen in Schlusstext"
                    : insertTarget === "mail-subject" || insertTarget === "nachfass-subject"
                      ? "Einfügen in Betreff"
                      : "Einfügen in E-Mail-Text"
              }
              className="xl:sticky xl:top-4 xl:self-start"
            >
              <div className="h-[28rem] overflow-hidden rounded-lg border border-separator">
                <GdPlaceholderPicker
                  categories={categories}
                  activeCategory={activeCategory}
                  onCategoryChange={setActiveCategory}
                  onInsert={insertToken}
                />
              </div>
            </Card>
          </div>
        )}
      </div>
    </AppShell>
  );
}
